import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import { Container, Spinner } from 'react-bootstrap';
import Turnstile from 'react-turnstile';

const API_URL = process.env.REACT_APP_API_URL;
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [email, setEmail] = useState('');
  const [captchaToken, setCaptchaToken] = useState('');
  const [loading, setLoading] = useState(!!token);
  const [submitting, setSubmitting] = useState(false);

  // Handle token-based verification
  useEffect(() => {
    if (!token) return;
    const verify = async () => {
      try {
        const res = await fetch(`${API_URL}/auth/verify`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token }),
        });

        const data = await res.json();

        if (res.ok) {
          toast.success(data.message || 'Email verified successfully!');
          setTimeout(() => (window.location.href = '/'), 2000);
        } else {
          toast.error(data.message || 'Verification failed.');
        }
      } catch {
        toast.error('Something went wrong.');
      } finally {
        setLoading(false);
      }
    };

    verify();
  }, [token]);

  // Manual verification flow
  const handleResend = async (e) => {
    e.preventDefault();
    if (!email || !captchaToken) {
      toast.error('Please complete the CAPTCHA and enter your email');
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/auth/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, captchaToken }),
      });

      const data = await res.json();
      if (res.ok) {
        toast.success('Verification email sent!');
      } else {
        toast.error(data.message || 'Failed to resend email');
      }
    } catch {
      toast.error('Error contacting server');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container className="mt-5">
      <ToastContainer />
      <div className="home-page-wrapper">
        <div className="login-container">
          {loading ? (
            <>
              <Spinner animation="border" className="mb-3" />
              <p>Verifying your email...</p>
            </>
          ) : token ? (
            <p>If this page doesn't redirect, go to the <a href="/login">login page</a>.</p>
          ) : (
            <>
              <h3>Resend verification email</h3>
              <p>Enter your email to receive a new verification link.</p>
              <form onSubmit={handleResend}>
                <div className="input-wrapper">
                  <input
                    type="email"
                    placeholder="you@example.com"
                    className={`input-control mb-3 ${!email ? 'error' : ''}`}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={submitting}
                  />
                  {!email && (
                    <span className="input-error">Please enter your email</span>
                  )}
                </div>

                <div className="input-wrapper">
                  <label className="input-label">Verify you're human</label>
                  <Turnstile
                    sitekey={TURNSTILE_SITE_KEY}
                    onVerify={(token) => setCaptchaToken(token)}
                    className="turnstile-wrapper"
                  />
                  {!captchaToken && (
                    <span className="input-error">Please complete the CAPTCHA</span>
                  )}
                </div>

                <button
                  className="btn btn-primary w-100 mt-3 d-flex align-items-center justify-content-center"
                  type="submit"
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Sending...
                    </>
                  ) : (
                    'Send Verification Email'
                  )}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </Container>
  );
}
